import { useState, Fragment, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import { v4 as uuid } from "uuid";
import { styles } from "../../shared-styles/styles";
import {
  AgencyQuestionGroup,
  BiometricMethod,
  Case,
  CreateEventInput,
  CreateParticipantAnswersInput,
  CreateParticipantAnswersV3Input,
  EventType,
  FaceValidationProblem,
  IdentifiableAnswerInput,
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  PendingQuestions,
  Platform,
  SaveRawEventInput,
  StepInput,
  ValidateParticipantImageInput,
  ValidateParticipantImageResponse,
} from "../../apis/API";
import DeviceInfoService from "../../utils/DeviceInfo";
import { SAVE_RAW_EVENT } from "../../apis/mutations/saveRawEvent";
import { CREATE_PARTICIPANT_ANSWERS_V2 } from "../../apis/mutations/createParticipantAnswersV2";
import { CREATE_PARTICIPANT_ANSWERS_V3 } from "../../apis/mutations/createParticipantAnswersV3";
import { CREATE_EVENT } from "../../apis/mutations/createEvent";
import { VALIDATE_IMAGE } from "../../apis/mutations/validateParticipantImage";
import LoadingIndicator from "../shared/LoadingIndicator";
import { strings } from "../../resources/strings";
import Utils, { getCurrentTimeInSeconds } from "../../utils/UtilityFunctions";
import CheckInPhotoStep from "./CheckInPhotoStep";
import CheckInQuestionsForm from "./CheckInQuestionsForm";
import NewQuestionsForm from "./NewQuestionsForm";
import { useQuestionFormsParticipantWebappFlag } from "../../utils/launchdarkly/featureFlags";
import { uploadData } from "@aws-amplify/storage";
import { S3UploadType } from "../../defs/Types";

interface CheckInModalProps {
  isOpen: boolean;
  isOnboarding?: boolean;
  isSelfTriggered?: boolean; // NEW: indicates self-triggered mode (no photo, promptId = null)
  sessionId?: string; // If onboarding, checkInId will be read from props
  checkInId?: string; // If not onboarding, checkInId will be generated
  participantCase?: Case | null;
  dismiss: () => void;
  removeStartCheckInButton: () => void;
  refreshCase: () => void; // Callback method to re-query the case after submitting Question Answers
  addOnboardingStep: (step: StepInput) => void; // Mutation to track the onboarding steps
  onboardingCompleted: () => void; // Callback method to close the onboarding modal
}

/**
 * State for one check-in photo submission attempt.
 *
 * Created once per capture and threaded through the flow rather than rebuilt on
 * every render, so results written across awaits (or from a mutation callback)
 * always land on the attempt that asked for them — never on a later one.
 */
interface CheckInFlowState {
  imageData: Uint8Array | null;
  errors: string[];
  validationResults: ValidateParticipantImageResponse | null;
  checkInEventCreated: boolean;
  faceDetected: boolean;
}

const createFlowState = (imageData: Uint8Array): CheckInFlowState => ({
  imageData,
  errors: [],
  validationResults: null,
  checkInEventCreated: false,
  faceDetected: false,
});

const CheckInProcess: React.FC<CheckInModalProps> = ({
  isOpen,
  isOnboarding,
  isSelfTriggered,
  sessionId,
  checkInId,
  participantCase,
  dismiss,
  removeStartCheckInButton,
  refreshCase,
  addOnboardingStep,
  onboardingCompleted,
}) => {
  // API - Mutations
  const [createParticipantAnswersMutation] = useMutation(
    CREATE_PARTICIPANT_ANSWERS_V2,
  );
  const [createParticipantAnswersV3Mutation] = useMutation(
    CREATE_PARTICIPANT_ANSWERS_V3,
  );
  const [saveRawEventMutation] = useMutation(SAVE_RAW_EVENT);
  const [createEventMutation] = useMutation(CREATE_EVENT);
  const [validateImageMutation] = useMutation(VALIDATE_IMAGE);

  // Fallback id for a non-onboarding check-in that wasn't given one. Generated
  // lazily and kept stable for the life of the component so every mutation in
  // one check-in reports the same id.
  const [fallbackCheckInId] = useState<string>(() => uuid());
  const activeCheckInId = isOnboarding
    ? sessionId!
    : (checkInId ?? fallbackCheckInId);

  // Incremented for every new attempt and every modal reset. An async
  // continuation whose token no longer matches belongs to a superseded
  // attempt and must not touch UI state.
  const attemptTokenRef = useRef<number>(0);
  // Single-flight guard: true from the moment a capture is accepted until that
  // attempt settles.
  const isSubmittingRef = useRef<boolean>(false);

  // Feature flag: controls which question form experience renders.
  // Defaults to off, in which case the existing form renders unchanged.
  const isNewQuestionFormsEnabled = useQuestionFormsParticipantWebappFlag();

  // UI states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Always start with webcam/photo step, even in self-triggered mode
  const [showWebCam, setShowWebCam] = useState<boolean>(true);
  const [showQuestions, setShowQuestions] = useState<boolean>(false);

  /**
   * Reset the check-in session when the modal opens/closes or the check-in id
   * changes. Bumping the attempt token invalidates any still-running attempt so
   * a late upload/validation result can't resume against the new session.
   * Always requires photo step before showing questions.
   */
  useEffect(() => {
    attemptTokenRef.current += 1;
    isSubmittingRef.current = false;

    if (isOpen) {
      setShowWebCam(true);
      setShowQuestions(false);
      setIsLoading(false);
    }
  }, [isOpen, activeCheckInId]);

  // Get the device info for the raw events
  const deviceInfo = DeviceInfoService.getInstance().getDeviceInfo();

  const questionGroup: AgencyQuestionGroup | null | undefined =
    participantCase?.questionAssignment?.agencyQuestionGroup;
  const participantQuestions:
    | (LocalizedParticipantQuestion | null)[]
    | null
    | undefined = questionGroup?.localizedParticipantQuestions;
  const pendingQuestions: PendingQuestions | null | undefined =
    participantCase?.pendingQuestions;

  const closeModal = () => {
    // If onboarding, call the parent mutation before dismissing
    if (isOnboarding) {
      onboardingCompleted();
    }
    dismiss();
    removeStartCheckInButton();
  };

  /**
   * Cancel the wizard via the toolbar close (✕) before answers are submitted.
   *
   * Unlike `closeModal`, this intentionally does NOT mark onboarding complete or
   * disable the Start button: answers are only persisted on final submit (there
   * is no partial save), so abandoning the form must leave the participant able
   * to be re-prompted. For onboarding, `onboardingCompletedAt` stays unset so the
   * onboarding flow (and its questions) returns on the next load.
   */
  const cancelWizard = () => {
    dismiss();
  };

  /**
   * Displays the loading indicator after submitting photo and questions
   */
  const renderSubmitting = () => {
    return (
      <div className="absolute inset-0 w-fit h-fit mx-auto my-auto">
        <LoadingIndicator />
        <h5 className={styles.modalHeader}>{strings.submitting}</h5>
      </div>
    );
  };

  /**
   * Called by the Child component `CheckInPhotoStep` after the photo has been captured
   * @param imageData - The image data captured from the webcam
   */
  const photoCaptured = (imageData: Uint8Array) => {
    // Single-flight: a repeated tap while an attempt is in progress is ignored
    // rather than starting a second upload/validation/check-in event.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    attemptTokenRef.current += 1;
    const attemptToken = attemptTokenRef.current;
    const flowState = createFlowState(imageData);

    // The submitting state is entered synchronously with the capture, so there
    // is no window in which the overlay can be scheduled after a fast failure
    // has already restored the photo step.
    setShowWebCam(false);
    setIsLoading(true);

    saveRawEvent(EventType.CheckInPhotoTaken, {
      checkInId: activeCheckInId,
      deviceInfo: deviceInfo,
    });

    startCheckInFlow(attemptToken, flowState);
  };

  /**
   * True when the attempt has been superseded by a newer attempt or by a modal
   * reset, in which case its continuation must not touch UI state.
   */
  const isStaleAttempt = (attemptToken: number) =>
    attemptToken !== attemptTokenRef.current;

  /**
   * Display the questions form
   * - Called after the check-in photo has been submitted and the Check-In event has been created
   */
  const displayQuestions = () => {
    setShowWebCam(false);
    setIsLoading(false);
    setShowQuestions(true);
    saveRawEvent(EventType.QuestionsPrompted, {
      checkInId: activeCheckInId,
      deviceInfo: deviceInfo,
    });
  };

  /**
   * Return the participant to a usable photo step
   * - Called on every failure path so a failed attempt is always retryable
   */
  const retakePhoto = () => {
    setShowQuestions(false);
    setIsLoading(false);
    setShowWebCam(true);
  };

  /**
   * Start the check-in flow after taking the photo
   * - Upload the image to S3, then call the mutation to validate the image with AWS Rekognition
   * - Read the validation results
   * - After successful validation, creates the Check-In event
   * - Prompts the user to answer questions
   */
  const startCheckInFlow = async (
    attemptToken: number,
    flowState: CheckInFlowState,
  ) => {
    try {
      // Each step returns false when the attempt cannot continue. Every such
      // path exits early and hands the participant back a retryable photo step
      // instead of leaving the submitting overlay up.
      const imageData = flowState.imageData;
      if (Utils.isNullOrEmpty(imageData)) {
        console.error("Failed to submit Check-In photo, imageData is null");
        Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
        retakePhoto();
        return;
      }

      const validated = await validateCheckInPhoto(imageData!, flowState);
      if (isStaleAttempt(attemptToken)) return;
      if (!validated) {
        retakePhoto();
        return;
      }

      // If a single face was detected (regardless of whether or not it was a
      // match), we'll still continue to submit the check-in photo
      if (!checkValidationResults(flowState)) {
        retakePhoto();
        return;
      }

      const photoSaved = await saveCheckInPhoto(imageData!);
      if (isStaleAttempt(attemptToken)) return;
      if (!photoSaved) {
        retakePhoto();
        return;
      }

      const eventCreated = await createCheckInEvent(flowState);
      if (isStaleAttempt(attemptToken)) return;
      if (!eventCreated) {
        retakePhoto();
        return;
      }

      saveRawEvent(EventType.CheckInSubmitted, {
        checkInId: activeCheckInId,
        errors: flowState.errors,
        deviceInfo: deviceInfo,
      });

      // Display questions if: (1) self-triggered mode, OR (2) there are pending questions
      if (isSelfTriggered || !Utils.isNullOrEmpty(pendingQuestions)) {
        displayQuestions();
      } else {
        closeModal();
      }
    } catch (error) {
      // Nothing above is expected to throw, but an unhandled one here would
      // strand the participant under the submitting overlay.
      console.error("Unexpected error during the check-in flow: ", error);
      if (isStaleAttempt(attemptToken)) return;
      Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
      retakePhoto();
    } finally {
      // A superseded attempt must not release the guard: the reset that
      // superseded it already did, and a newer attempt may now hold it.
      if (!isStaleAttempt(attemptToken)) {
        isSubmittingRef.current = false;
      }
    }
  };

  /**
   * Upload file to S3, then call the mutation to validate the image with the S3 key
   * @param data - The captured image data
   * @param flowState - State for the attempt being submitted
   * @returns true when the validation call completed and its results are available
   */
  const validateCheckInPhoto = async (
    data: Uint8Array,
    flowState: CheckInFlowState,
  ): Promise<boolean> => {
    const s3Key = `temp-images/${activeCheckInId}.png`;

    try {
      await uploadData({
        key: s3Key,
        data: data,
        options: {
          contentType: S3UploadType.PNG,
        },
      }).result;
    } catch (error) {
      console.error("Error uploading check-in photo: ", error);
      Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
      return false;
    }

    // Call the mutation to validate the image with the S3 key
    const input: ValidateParticipantImageInput = {
      s3ImagePath: s3Key,
    };

    return await performImageValidationMutation(input, flowState);
  };

  /**
   * Check the validation results from the image validation mutation
   * - If the face matched, we'll submit the check-in results via createEvent
   * - If the face did not match, we'll display an error message and retake the photo
   */
  const checkValidationResults = (flowState: CheckInFlowState): boolean => {
    if (Utils.isNullOrEmpty(flowState.validationResults)) {
      console.error("Check-In photo validation returned no results");
      Utils.toastErrorAutoClose(strings.toast.generalError);
      return false;
    }
    const imageValidationResults = flowState.validationResults!;
    const imageValidationError = imageValidationResults.validationProblem;

    // Check validation errors - If face wasn't a match, continue to submit the check-in
    // photo, as this will display as a failed check-in in the agency webapp
    if (!Utils.isNullOrEmpty(imageValidationError)) {
      switch (imageValidationError) {
        case FaceValidationProblem.FaceDoesNotMatch:
          flowState.faceDetected = true;
          break;

        case FaceValidationProblem.MultipleFaces:
          Utils.toastErrorAutoClose(strings.toast.multipleFacesDetected);
          break;

        case FaceValidationProblem.NoFaces:
          Utils.toastErrorAutoClose(strings.toast.noFaceDetected);
          break;

        default:
          break;
      }
      addError(flowState, imageValidationError!.toString());
    } else {
      flowState.faceDetected = true;
    }

    return flowState.faceDetected;
  };

  /**
   * Upload the check-in photo to S3
   * @param data - The captured image data
   * @returns true when the photo was stored
   */
  const saveCheckInPhoto = async (data: Uint8Array): Promise<boolean> => {
    try {
      await uploadData({
        key: `checkins/${activeCheckInId}.png`,
        data: data,
        options: {
          contentType: S3UploadType.PNG,
        },
      }).result;

      if (isOnboarding) {
        addOnboardingStep({ index: 1, name: "photo" });
      }
      return true;
    } catch (error) {
      console.log("Error saving check-in photo! Error: ", error);
      Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
      return false;
    }
  };

  /**
   * After validating and saving the check-in photo, create the check-in event
   * @param flowState - State for the attempt being submitted
   * @returns true when the check-in event was created
   */
  const createCheckInEvent = async (
    flowState: CheckInFlowState,
  ): Promise<boolean> => {
    const input: CreateEventInput = {
      id: activeCheckInId,
      type: EventType.CheckIn,
      time: getCurrentTimeInSeconds(),
    };

    try {
      await createEventMutation({
        variables: {
          input: {
            id: input.id,
            type: input.type,
            time: input.time,
          },
        },
        onCompleted: () => {
          flowState.checkInEventCreated = true;
          Utils.toastSuccess(strings.toast.checkInPhotoSuccess);
        },
      });
    } catch (error) {
      console.error("Error creating check-in event: ", error);
      Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
      return false;
    }

    if (!flowState.checkInEventCreated) {
      console.error("Check-In event was not created");
      Utils.toastErrorAutoClose(strings.toast.checkInPhotoError);
      return false;
    }
    return true;
  };

  /**
   * Add an error to the current attempt state
   * @param flowState - State for the attempt being submitted
   * @param error - The error message to add
   */
  const addError = (flowState: CheckInFlowState, error: string) => {
    flowState.errors.push(error);
  };

  /**
   * Mutation to validate the participant's check-in photo
   * - stores the validation results on the attempt from the awaited mutation response
   * @returns true when the mutation completed without error
   */
  const performImageValidationMutation = async (
    input: ValidateParticipantImageInput,
    flowState: CheckInFlowState,
  ): Promise<boolean> => {
    try {
      const { data } = await validateImageMutation({
        variables: {
          input: {
            s3ImagePath: input.s3ImagePath,
          },
        },
      });
      const response = data?.validateParticipantImage;
      if (!Utils.isNullOrEmpty(response)) {
        flowState.validationResults = response as ValidateParticipantImageResponse;
      }
      return true;
    } catch (error) {
      console.error("An error occurred while validating the image: ", error);
      Utils.toastErrorAutoClose(strings.toast.generalError);
      return false;
    }
  };

  /**
   * Async function which performs the mutation to save raw events
   * @param type - The type of event to save
   */
  const saveRawEvent = async (type: EventType, data?: any) => {
    const input: SaveRawEventInput = {
      id: data.checkInId,
      type: type,
      clientTime: getCurrentTimeInSeconds(),
      data: JSON.stringify(data),
    };
    await performSaveRawEventMutation(input);
  };

  /**
   * Mutation to save raw events
   * - onCompleted callback will close the modal and display success toast message
   */
  const performSaveRawEventMutation = async (input: SaveRawEventInput) => {
    try {
      await saveRawEventMutation({
        variables: {
          input: {
            id: input.id,
            type: input.type,
            clientTime: input.clientTime,
            data: input.data,
          },
        },
      });
    } catch (error) {
      console.error("Error saving raw event: ", error);
    }
  };

  /**
   * Submit Answers button callback function
   * - Only clickable when all questions have been answered
   */
  const submitAnswers = async (questionAnswers: IdentifiableAnswerInput[]) => {
    setShowQuestions(false);
    setIsLoading(true);

    // Build the mutation input object
    // In self-triggered mode promptId is null
    const mutationInput: CreateParticipantAnswersInput = {
      promptId: isSelfTriggered ? null : (pendingQuestions?.promptId ?? ""),
      questionGroupId: questionGroup?.id ?? "",
      createdAt: getCurrentTimeInSeconds(),
      checkInId: activeCheckInId,
      identifiableAnswers: questionAnswers,
    };

    // Call the function to perform the mutation
    await performCreateParticipantAnswersMutation(mutationInput);
  };

  /**
   * Mutation to save the participant's answers to the questions
   * - Uses V2 mutation which requires caseId and app parameters
   * - onCompleted callback will close the modal and display success toast message
   */
  const performCreateParticipantAnswersMutation = async (
    input: CreateParticipantAnswersInput,
  ) => {
    try {
      await createParticipantAnswersMutation({
        variables: {
          input,
          caseId: participantCase?.caseId ?? "",
          app: {
            platform: "Web",
            version: "1.0",
          },
        },
        onCompleted: (data) => {
          // Close the modal on complete
          setTimeout(() => {
            closeModal();
            // Submitted Successfully
            if (data?.createParticipantAnswersV2 === true) {
              Utils.toastSuccess(strings.toast.questionAnswersSuccess);

              // Refresh `Case` in the parent component
              refreshCase();

              saveRawEvent(EventType.AgencyQuestionAnswer, {
                checkInId: activeCheckInId,
                deviceInfo: deviceInfo,
              });
            } else {
              // We didn't catch an error from the API, so we'll display a generic error message
              Utils.toastError(strings.toast.questionAnswersError);
            }
          }, 1000);
        },
      });
    } catch (error) {
      console.error(
        "[performCreateParticipantAnswersMutation] Error submitting question answers: ",
        error,
      );
      Utils.toastError(strings.toast.questionAnswersError);
      setTimeout(() => {
        closeModal();
      }, 1000);
    }
  };

  /**
   * Submit the new Question Forms wizard answers via createParticipantAnswersV3.
   * Assembles the V3 payload around the flow-engine answers (which already carry
   * questionType + skipped) using the same field sources as the V2 path.
   *
   * Unlike the V2 submit, this does NOT close the modal or toggle showQuestions:
   * the wizard owns its own loading/success/error UI and stays mounted, so a
   * failed submit keeps the gathered answers for retry. Returns true on success,
   * false on failure — never throws (the wizard branches on the boolean).
   *
   * `biometric: { method: none }` because web check-ins capture no device
   * biometric (face/finger); the check-in photo is linked via `checkInId`.
   * `signerName` is the participant's typed attestation from the Final Statement.
   */
  const submitQuestionFormV3 = async (
    answers: IdentifiableAnswerV3Input[],
    meta: { signerName: string },
  ): Promise<boolean> => {
    const caseId = participantCase?.caseId;
    const questionGroupId = questionGroup?.id;
    // caseId and questionGroupId are required (non-null) by the V3 input. If
    // either is missing, fail fast rather than send a request with empty-string
    // ids that the backend would reject.
    if (!caseId || !questionGroupId) {
      console.error(
        "[submitQuestionFormV3] Missing required case context (caseId/questionGroupId)",
      );
      Utils.toastError(strings.toast.questionAnswersError);
      return false;
    }

    const now = getCurrentTimeInSeconds();
    const input: CreateParticipantAnswersV3Input = {
      promptId: isSelfTriggered ? null : (pendingQuestions?.promptId ?? null),
      questionGroupId,
      createdAt: now,
      checkInId: activeCheckInId,
      biometric: { method: BiometricMethod.none },
      signerName: meta.signerName.trim(),
      signedAt: now,
      identifiableAnswers: answers,
    };

    try {
      const { data } = await createParticipantAnswersV3Mutation({
        variables: {
          input,
          caseId,
          app: { platform: Platform.Web, version: "1.0" },
        },
      });

      if (data?.createParticipantAnswersV3 === true) {
        Utils.toastSuccess(strings.toast.questionAnswersSuccess);
        refreshCase();
        saveRawEvent(EventType.AgencyQuestionAnswer, {
          checkInId: activeCheckInId,
          deviceInfo: deviceInfo,
        });
        return true;
      }

      Utils.toastError(strings.toast.questionAnswersError);
      return false;
    } catch (error) {
      console.error(
        "[submitQuestionFormV3] Error submitting question answers: ",
        error,
      );
      Utils.toastError(strings.toast.questionAnswersError);
      return false;
    }
  };
  return (
    <Transition show={isOpen} as={Fragment}>
      {/*
            * Setting `static` and a null close handler prevents the user from dismissing the dialog by
                clicking outside or pressing the `ESC` key.
            */}
      <Dialog static onClose={() => null}>
        {/*
         * The backdrop - rendered as a fixed sibling to the panel container
         * Rendered with fade-in/out transitions
         */}
        <Transition.Child
          as={Fragment}
          enter="ease-in duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/90" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="transition-all ease-in-out duration-200"
          enterFrom="opacity-0 scale-50"
          enterTo="opacity-100 scale-100"
          leave="transition-all ease-in-out duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-50"
        >
          {/* Scrollable container */}
          <div className="fixed inset-0 w-screen overflow-y-auto  transition-all duration-300">
            {/* Container to center the panel */}
            <div className="flex min-h-full min-w-full items-center justify-center ">
              <Dialog.Panel className="min-w-[60%] max-w-[90%] min-h-[40vh] p-5 m-5 rounded-xl border-none shadow-md drop-shadow-md bg-white">
                {/* Loading/Submitting state */}
                {isLoading && renderSubmitting()}

                {/* First Step - CheckIn Photo */}
                {showWebCam && (
                  <>
                    <Dialog.Title>
                      <div className={styles.modalHeader}>
                        {strings.checkInModalTitle}
                      </div>
                    </Dialog.Title>
                    <Dialog.Description>
                      {strings.checkInPhotoNote}
                    </Dialog.Description>
                    <CheckInPhotoStep
                      photoCaptured={photoCaptured}
                      isSubmitting={isLoading}
                    />
                  </>
                )}

                {/* Second Step - Questions */}
                {showQuestions &&
                  participantQuestions &&
                  (isNewQuestionFormsEnabled ? (
                    // The new wizard owns its own "Report Form" toolbar chrome,
                    // so the legacy visible modal title/description are dropped.
                    // A visually-hidden Title/Description are still rendered so
                    // the Dialog keeps an accessible name for screen readers.
                    <>
                      <Dialog.Title className="sr-only">
                        {strings.reportForm.title}
                      </Dialog.Title>
                      <Dialog.Description className="sr-only">
                        {strings.reportForm.welcomeBody}
                      </Dialog.Description>
                      <NewQuestionsForm
                        participantQuestions={participantQuestions}
                        onSubmit={submitQuestionFormV3}
                        onClose={cancelWizard}
                        onSubmitted={closeModal}
                        groupName={questionGroup?.name}
                        displaySectionGroupNames={
                          questionGroup?.displaySectionGroupNames
                        }
                        questionPrepopulation={
                          participantCase?.questionPrepopulation
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Dialog.Title>
                        <div className={styles.modalHeader}>
                          {isSelfTriggered
                            ? strings.updateAnswersModalTitle
                            : strings.questionsModalTitle}
                        </div>
                      </Dialog.Title>
                      <Dialog.Description>
                        {isSelfTriggered
                          ? strings.updateAnswersModalText
                          : strings.questionsModalText}
                      </Dialog.Description>
                      <CheckInQuestionsForm
                        participantQuestions={participantQuestions}
                        submitAnswers={submitAnswers}
                      />
                    </>
                  ))}
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default CheckInProcess;
